import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../utils/apiUtils";
import { clearCorporateSession, getCorporateToken } from "../utils/corporateSession";
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaBriefcase, 
  FaBuilding, 
  FaMapMarkerAlt, 
  FaCalendarAlt, 
  FaIdCard, 
  FaWallet, 
  FaCamera, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaSpinner, 
  FaUsers, 
  FaGlobe, 
  FaHeart
} from "react-icons/fa";

const CorporateProfile = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const [profileForm, setProfileForm] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    gstNumber: "",
    dob: "",
    gender: "Male",
    maritalStatus: "Single",
    nationality: "India",
    designation: "",
    address: {
      fullAddress: "",
      city: "",
      state: "",
      pincode: "",
    },
  });

  const [wallet, setWallet] = useState(null);

  const fetchWallet = async () => {
    try {
      const res = await api.get("/corporate/wallet");
      if (res?.data?.success) {
        setWallet(res.data.data);
      }
    } catch (e) {
      console.error("Failed to fetch wallet in corporate profile", e);
    }
  };

  const fetchMe = async () => {
    setLoading(true);
    try {
      const res = await api.get("/corporate/me");
      if (res?.data?.success) {
        const data = res.data.data;
        setMe(data);
        setProfileForm({
          fullName: String(data?.fullName ?? ""),
          email: String(data?.email ?? ""),
          mobileNumber: String(data?.mobileNumber ?? ""),
          gstNumber: String(data?.gstNumber ?? ""),
          dob: data?.dob ? data.dob.split('T')[0] : "",
          gender: data?.gender || "Male",
          maritalStatus: data?.maritalStatus || "Single",
          nationality: data?.nationality || "India",
          designation: String(data?.designation ?? ""),
          address: {
            fullAddress: typeof data?.address === "string" ? data.address : (data?.address?.fullAddress ?? ""),
            city: String(data?.city ?? ""),
            state: String(data?.state ?? ""),
            pincode: String(data?.pincode ?? ""),
          },
        });
      } else {
        toast.error(res?.data?.message || "Failed to load profile");
        clearCorporateSession();
        navigate("/corporate/login", { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to load profile");
      clearCorporateSession();
      navigate("/corporate/login", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = getCorporateToken();
    if (!token) {
      navigate("/corporate/login", { replace: true });
      return;
    }

    fetchMe();
    fetchWallet();
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const addressField = name.split(".")[1];
      setProfileForm((s) => ({
        ...s,
        address: { ...s.address, [addressField]: value },
      }));
    } else {
      setProfileForm((s) => ({ ...s, [name]: value }));
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image file is too large. Maximum size is 5MB.");
      return;
    }

    const formData = new FormData();
    formData.append("profileImage", file);

    setImageUploading(true);
    try {
      const res = await api.put("/corporate/profile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (res?.data?.success) {
        toast.success("Profile image updated successfully!");
        const updatedMember = res?.data?.data;
        if (updatedMember) {
          setMe(updatedMember);
          localStorage.setItem("corporateMember", JSON.stringify(updatedMember));
          window.dispatchEvent(new Event("corporateSessionChanged"));
        }
      } else {
        toast.error(res?.data?.message || "Failed to upload image");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to upload image");
    } finally {
      setImageUploading(false);
    }
  };

  const saveProfile = async (e) => {
    e.preventDefault();

    if (!["admin", "staff"].includes(me?.role)) {
      toast.error("Only corporate members can update profile");
      return;
    }

    if (!profileForm.fullName) {
      toast.error("Full Name is required");
      return;
    }

    const payload = {
      fullName: profileForm.fullName,
      email: profileForm.email,
      mobileNumber: profileForm.mobileNumber,
      gstNumber: profileForm.gstNumber,
      dob: profileForm.dob,
      gender: profileForm.gender,
      maritalStatus: profileForm.maritalStatus,
      nationality: profileForm.nationality,
      designation: profileForm.designation,
      // Flatten address fields as expected by the backend controller
      address: profileForm.address.fullAddress,
      city: profileForm.address.city,
      state: profileForm.address.state,
      pincode: profileForm.address.pincode,
    };

    try {
      const res = await api.put("/corporate/profile", payload);
      if (res?.data?.success) {
        toast.success(res.data.message || "Profile updated successfully");
        const updatedMember = res?.data?.data;
        if (updatedMember) {
          setMe(updatedMember);
          localStorage.setItem("corporateMember", JSON.stringify(updatedMember));
          if (updatedMember.gender) {
            localStorage.setItem("userGender", updatedMember.gender);
          }
          window.dispatchEvent(new Event("corporateSessionChanged"));
        }
        setIsEditing(false);
      } else {
        toast.error(res?.data?.message || "Failed to update profile");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to update profile");
    }
  };

  const getInitials = (name) => {
    if (!name) return "C";
    return name.charAt(0).toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <FaSpinner className="animate-spin text-[#F56753] text-4xl mb-4" />
        <div className="text-gray-500 font-medium">Fetching profile details...</div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <div className="text-red-500 text-xl font-semibold mb-2">Access Denied</div>
        <div className="text-gray-600 mb-4">Please log in again to view your corporate profile.</div>
        <button 
          onClick={() => navigate("/corporate/login")} 
          className="px-6 py-2 bg-[#F56753] hover:bg-[#d94e3a] text-white rounded-lg transition font-medium"
        >
          Go to Login
        </button>
      </div>
    );
  }

  const isEditableRole = ["admin", "staff"].includes(me?.role);

  return (
    <div className="w-full max-w-7xl mx-auto py-2">
      {/* Upper Navigation / Actions Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Corporate Profile</h1>
          <p className="text-sm text-slate-500 mt-1">Manage your account information and preferences</p>
        </div>

        {isEditableRole && (
          <div className="flex items-center gap-3">
            {isEditing ? (
              <>
                <button 
                  onClick={() => {
                    setIsEditing(false);
                    fetchMe(); // Reset changes
                  }}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl transition duration-200 text-sm font-medium"
                >
                  <FaTimes /> Cancel
                </button>
                <button 
                  onClick={saveProfile}
                  className="flex items-center gap-2 px-5 py-2 bg-[#F56753] hover:bg-[#d94e3a] text-white shadow-md shadow-red-200 rounded-xl transition duration-200 text-sm font-semibold"
                >
                  <FaSave /> Save Changes
                </button>
              </>
            ) : (
              <button 
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-5 py-2 bg-slate-900 hover:bg-slate-800 text-white shadow-md shadow-slate-300 rounded-xl transition duration-200 text-sm font-semibold"
              >
                <FaEdit /> Edit Profile
              </button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Column - Hero Profile Card & Wallet Balance */}
        <div className="lg:col-span-1 space-y-8">
          
          {/* Hero Profile Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-100 border border-slate-100 overflow-hidden relative transition-all duration-300 hover:shadow-2xl">
            {/* Soft decorative header gradient */}
            <div className="h-28 bg-gradient-to-r from-[#F56753] via-red-800 to-[#d94e3a] relative"></div>
            
            <div className="px-6 pb-6 pt-0 flex flex-col items-center -mt-14 relative z-10">
              
              {/* Profile Image with Camera Hover Trigger */}
              <div className="relative group cursor-pointer" onClick={triggerFileInput}>
                <div className="w-28 h-28 rounded-full border-4 border-white bg-slate-100 shadow-lg overflow-hidden flex items-center justify-center text-white relative">
                  {imageUploading ? (
                    <div className="absolute inset-0 bg-slate-950/40 flex items-center justify-center">
                      <FaSpinner className="animate-spin text-white text-2xl" />
                    </div>
                  ) : me?.profileImageUrl ? (
                    <img 
                      src={me.profileImageUrl} 
                      alt={me.fullName || "User"} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#F56753] to-[#d94e3a] flex items-center justify-center text-3xl font-bold">
                      {getInitials(me.fullName || me.email)}
                    </div>
                  )}

                  {/* Dark hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity duration-200">
                    <FaCamera className="text-white text-xl mb-1" />
                    <span className="text-[10px] text-white/90 font-medium tracking-wide uppercase">Change</span>
                  </div>
                </div>
              </div>

              {/* Hidden File Input */}
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleImageUpload} 
                accept="image/*" 
                className="hidden" 
              />

              {/* User Identity Details */}
              <h3 className="mt-4 text-xl font-bold text-slate-800 text-center">{me.fullName || "Corporate Member"}</h3>
              <p className="text-xs text-slate-400 font-medium tracking-wider uppercase mt-0.5">{me.designation || "No Designation Set"}</p>
              
              {/* Glowing Badge for Role */}
              <span className={`mt-3 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide ${
                me.role === "admin" 
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-100 shadow-sm shadow-emerald-50" 
                  : "bg-purple-50 text-purple-700 border border-purple-100 shadow-sm shadow-purple-50"
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full mr-2 ${me.role === "admin" ? "bg-emerald-500" : "bg-purple-500 animate-pulse"}`}></span>
                Corporate {me.role === "admin" ? "Admin" : "Staff"}
              </span>

              {/* Inline quick metadata list */}
              <div className="w-full border-t border-slate-100 mt-6 pt-5 space-y-3">
                <div className="flex items-center gap-3 text-slate-600 text-sm">
                  <FaEnvelope className="text-slate-400 flex-shrink-0" />
                  <span className="truncate" title={me.email}>{me.email}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600 text-sm">
                  <FaPhone className="text-slate-400 flex-shrink-0" />
                  <span>{me.mobileNumber || "Not Linked"}</span>
                </div>
                <div className="flex items-center gap-3 text-slate-600 text-sm">
                  <span className={`w-2 h-2 rounded-full ${me.profileCompleted ? "bg-green-500" : "bg-amber-500"}`}></span>
                  <span className="font-medium text-slate-500">
                    Profile Status: <strong className={me.profileCompleted ? "text-green-600" : "text-amber-600"}>
                      {me.profileCompleted ? "Completed" : "Incomplete"}
                    </strong>
                  </span>
                </div>
              </div>

            </div>
          </div>

          {/* Premium Account Card (Wallet/Credit Details) */}
          <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-red-950 rounded-2xl shadow-xl p-6 text-white border border-slate-700/30 relative overflow-hidden transition-all duration-300 hover:shadow-2xl">
            {/* Background glowing circle decorator */}
            <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#F56753]/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <div className="flex items-center justify-between mb-6">
              <span className="text-xs font-bold tracking-widest uppercase text-red-200">Cabzigo Wallet Summary</span>
              <FaWallet className="text-red-400 text-2xl" />
            </div>

            <div className="space-y-5">
              {me.billingModel === 'wallet' ? (
                <>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">Available Wallet Balance</p>
                    <h4 className="text-3xl font-extrabold text-green-400 mt-1">
                      ₹{(wallet ? wallet.totalAvailable : (me.walletBalance || 0)).toLocaleString("en-IN")}
                    </h4>
                  </div>
                  <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 text-xs text-slate-300 mt-2">
                    <span className="font-semibold text-slate-200">Billing Model:</span> <span className="capitalize">{me.billingModel || "Not configured"}</span>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-xs text-slate-400 font-medium">Available Balance</p>
                    <h4 className="text-3xl font-extrabold text-green-400 mt-1">
                      ₹{Math.max(0, wallet ? wallet.totalAvailable : ((me.creditLimit || 0) + (me.walletBalance || 0) - (me.creditUsed || 0))).toLocaleString("en-IN")}
                    </h4>
                    <p className="text-[10px] text-slate-400 mt-1">
                      (Limit + Funds - Credit Used)
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 border-t border-slate-700/60 pt-4">
                    <div>
                      <p className="text-[9px] text-slate-400 font-semibold tracking-wider uppercase">Credit Limit</p>
                      <p className="text-sm font-bold text-slate-200 mt-0.5">₹{(wallet ? wallet.creditLimit : (me.creditLimit || 0)).toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-semibold tracking-wider uppercase">Added Funds</p>
                      <p className="text-sm font-bold text-emerald-400 mt-0.5">₹{(wallet ? wallet.addedFunds : (me.walletBalance || 0)).toLocaleString("en-IN")}</p>
                    </div>
                    <div>
                      <p className="text-[9px] text-slate-400 font-semibold tracking-wider uppercase">Credit Used</p>
                      <p className="text-sm font-bold text-rose-400 mt-0.5">₹{(wallet ? (wallet.creditUsed + wallet.reservedFunds) : (me.creditUsed || 0)).toLocaleString("en-IN")}</p>
                    </div>
                  </div>

                  <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-3 text-xs text-slate-300 mt-2">
                    <span className="font-semibold text-slate-200">Billing Model:</span> <span className="capitalize">{me.billingModel?.replace('_', ' ') || "Not configured"}</span>
                  </div>
                </>
              )}
            </div>
          </div>

        </div>

        {/* Right Column - Personal, Address, and Company Information Panel */}
        <div className="lg:col-span-2 space-y-8">
          
          <form onSubmit={saveProfile} className="space-y-8">
            
            {/* Card 1: Personal Details */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-100 border border-slate-100 p-6 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-6">
                <FaUser className="text-[#F56753] text-lg" />
                <h2 className="text-xl font-bold text-slate-800">Personal Information</h2>
              </div>

              {isEditing ? (
                /* Editable Inputs */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Full Name</label>
                    <input
                      name="fullName"
                      value={profileForm.fullName}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Email Address</label>
                    <input
                      name="email"
                      type="email"
                      value={profileForm.email}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Mobile Number</label>
                    <input
                      name="mobileNumber"
                      value={profileForm.mobileNumber}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                      placeholder="e.g., +91 9876543210"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Date of Birth</label>
                    <input
                      name="dob"
                      type="date"
                      value={profileForm.dob}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Gender</label>
                    <select
                      name="gender"
                      value={profileForm.gender}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Marital Status</label>
                    <select
                      name="maritalStatus"
                      value={profileForm.maritalStatus}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                    >
                      <option value="Single">Single</option>
                      <option value="Married">Married</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Nationality</label>
                    <input
                      name="nationality"
                      value={profileForm.nationality}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Designation</label>
                    <input
                      name="designation"
                      value={profileForm.designation}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                      placeholder="e.g., Senior Analyst"
                    />
                  </div>
                </div>
              ) : (
                /* Readonly View Grid */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaUser className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Full Name</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.fullName || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaEnvelope className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.email || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaPhone className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Mobile Number</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.mobileNumber || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaCalendarAlt className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Date of Birth</span>
                      <span className="text-sm font-bold text-slate-700">
                        {profileForm.dob ? new Date(profileForm.dob).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "-"}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaUser className="text-xs" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Gender</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.gender || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaHeart className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Marital Status</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.maritalStatus || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaGlobe className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Nationality</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.nationality || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaBriefcase className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Designation</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.designation || "-"}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Card 2: Address Information */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-100 border border-slate-100 p-6 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-6">
                <FaMapMarkerAlt className="text-[#F56753] text-lg" />
                <h2 className="text-xl font-bold text-slate-800">Address Information</h2>
              </div>

              {isEditing ? (
                /* Editable Address Inputs */
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">Full Address</label>
                    <input
                      name="address.fullAddress"
                      value={profileForm.address.fullAddress}
                      onChange={onChange}
                      className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                      placeholder="e.g., Street Name, Flat / Building"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">City</label>
                      <input
                        name="address.city"
                        value={profileForm.address.city}
                        onChange={onChange}
                        className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                        placeholder="e.g., Mumbai"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wider">State</label>
                      <input
                        name="address.state"
                        value={profileForm.address.state}
                        onChange={onChange}
                        className="w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                        placeholder="e.g., Maharashtra"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-505 mb-1.5 uppercase tracking-wider">Pincode</label>
                      <input
                        name="address.pincode"
                        value={profileForm.address.pincode}
                        onChange={onChange}
                        className="w-full border border-slate-250 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-xl px-4 py-2.5 transition text-slate-800 text-sm font-medium"
                        placeholder="e.g., 400001"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                /* Readonly Address View */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3.5 md:col-span-2">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaMapMarkerAlt className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Full Address</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.address.fullAddress || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaBuilding className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">City</span>
                      <span className="text-sm font-bold text-slate-700">{profileForm.address.city || "-"}</span>
                    </div>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaGlobe className="text-sm" /></div>
                    <div>
                      <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">State & Pincode</span>
                      <span className="text-sm font-bold text-slate-700">
                        {profileForm.address.state ? `${profileForm.address.state}${profileForm.address.pincode ? ` - ${profileForm.address.pincode}` : ""}` : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Card 3: Company & Billing Details (Always Read-only) */}
            <div className="bg-white rounded-2xl shadow-xl shadow-slate-100 border border-slate-100 p-6 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center gap-3 border-b border-slate-100 pb-4 mb-6">
                <FaBuilding className="text-emerald-500 text-lg" />
                <h2 className="text-xl font-bold text-slate-800">Company & Billing Details</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaBuilding className="text-sm" /></div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Company Name</span>
                    <span className="text-sm font-bold text-slate-700">{me.companyName || "-"}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaIdCard className="text-sm" /></div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">GST Number</span>
                    {isEditing && me.role === "admin" ? (
                      <input 
                        name="gstNumber"
                        value={profileForm.gstNumber}
                        onChange={onChange}
                        className="mt-1 w-full border border-slate-200 focus:border-[#F56753] focus:ring-1 focus:ring-[#F56753] rounded-lg px-2.5 py-1 text-sm font-medium"
                      />
                    ) : (
                      <span className="text-sm font-bold text-slate-700">{me.gstNumber || "-"}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaUsers className="text-sm" /></div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Employees</span>
                    <span className="text-sm font-bold text-slate-700">{me.totalEmployees || "0"}</span>
                  </div>
                </div>

                <div className="flex items-start gap-3.5">
                  <div className="p-2.5 bg-slate-50 text-slate-500 rounded-xl"><FaWallet className="text-sm" /></div>
                  <div>
                    <span className="block text-xs font-semibold text-slate-400 uppercase tracking-wider">Billing Model</span>
                    <span className="text-sm font-bold text-slate-700 capitalize">{me.billingModel || "-"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Actions for Editing Mode */}
            {isEditing && (
              <div className="flex justify-end gap-3 mt-6">
                <button 
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    fetchMe();
                  }}
                  className="px-6 py-2.5 border border-slate-200 text-slate-600 font-semibold rounded-xl hover:bg-slate-50 transition duration-200 text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-8 py-2.5 bg-[#F56753] hover:bg-[#d94e3a] text-white font-semibold rounded-xl shadow-lg shadow-red-200 transition duration-200 text-sm"
                >
                  Save Profile
                </button>
              </div>
            )}

          </form>

        </div>

      </div>
    </div>
  );
};

export default CorporateProfile;
